import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import {
  listAdminUploads,
  reconcileAdminUpload,
  retryAdminUpload
} from '../service'
import { uploadStatusLabel } from '../components/upload/UploadProcessingStatus'
import CoachingBetaPanel from '../components/admin/CoachingBetaPanel'
import ContactRequestsPanel from '../components/admin/ContactRequestsPanel'

const dateLabel = value => {
  if (!value) return 'Never'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString()
}

function Admin () {
  const [uploads, setUploads] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [message, setMessage] = useState(null)

  const load = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true)
      setError(null)
      setUploads(await listAdminUploads({ limit: 500 }))
    } catch (err) {
      console.error(err)
      setError(err)
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load({ quiet: true }), 15000)
    return () => window.clearInterval(timer)
  }, [load])

  const runAction = async (upload, action) => {
    setBusyId(upload.id)
    setMessage(null)
    try {
      const result = action === 'retry'
        ? await retryAdminUpload(upload.id)
        : await reconcileAdminUpload(upload.id)
      setMessage({
        severity: 'success',
        text: `${upload.name}: ${result.action || action}`
      })
      await load({ quiet: true })
    } catch (err) {
      console.error(err)
      setMessage({
        severity: 'error',
        text: `${upload.name}: ${err.message || String(err)}`
      })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1800, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Upload Operations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inspect processing state, reconcile stale uploads, and retry safe processing jobs.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => load()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      <ContactRequestsPanel />

      <CoachingBetaPanel />

      {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
      {error
        ? <Alert severity="error">
            {String(error.message || error)}
          </Alert>
        : null}

      {loading
        ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        : null}

      {!loading && !error
        ? <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Upload</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Last status</TableCell>
                    <TableCell>Job</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploads.map(upload => (
                    <TableRow key={upload.id} hover>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {upload.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {upload.owner || 'Unknown owner'} · {upload.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={uploadStatusLabel(upload)}
                          color={upload.readyForPlayback ? 'success' : upload.status === 'failed' ? 'error' : 'default'}
                          variant={upload.readyForPlayback ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{upload.processingPhase || '-'}</TableCell>
                      <TableCell>
                        {Number.isFinite(upload.processingPercent)
                          ? `${upload.processingPercent}%`
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {dateLabel(upload.statusUpdatedAt || upload.updatedAt)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {upload.mediaJobId || '-'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 340 }}>
                        <Typography variant="caption" color={upload.processingError ? 'error' : 'text.secondary'}>
                          {upload.processingErrorCode
                            ? `${upload.processingErrorCode}: ${upload.processingError || ''}`
                            : upload.processingError || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<BuildCircleOutlinedIcon />}
                            disabled={busyId === upload.id}
                            onClick={() => runAction(upload, 'reconcile')}
                          >
                            Reconcile
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            startIcon={<ReplayIcon />}
                            disabled={busyId === upload.id || upload.status !== 'failed'}
                            onClick={() => runAction(upload, 'retry')}
                          >
                            Retry
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!uploads.length
                    ? <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No uploads found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        : null}
    </Stack>
  )
}

export default Admin
